import React, { useState, useRef } from "react";
import { Card, Form, Input, Button, Typography, Space, message } from "antd";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

const Signup = () => {
  const [stage, setStage] = useState("signup"); // 'signup', 'verifyOtp'
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const otpRefs = useRef([]); // Create refs for OTP inputs
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  // Signup form state
  const [form] = Form.useForm();
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d*$/.test(value)) return; // Only allow numeric input

    const newOtp = [...otp];
    newOtp[index] = value;

    setOtp(newOtp);

    // Move focus to the next input if a digit is entered
    if (value && index < otpRefs.current.length - 1) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    // Handle backspace to move focus to the previous input
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handleRequestOtp = async (values) => {
    const { email, password, name } = values;

    setIsLoading(true);

    try {
      const response = await fetch(
        `https://api.21stcenturyav.info/signup/request-otp/?email=${email}`,
        {
          method: "POST",
        }
      );

      setIsLoading(false);
      if (response.ok) {
        message.success("OTP sent to your email. Please verify to continue.");
        setStage("verifyOtp");
        localStorage.setItem("signupDetails", JSON.stringify({ email, password, name }));
      } else {
        message.error("Failed to send OTP. Please check the email.");
      }
    } catch (error) {
      console.error("Request OTP error:", error);
      setIsLoading(false);
      message.error("An error occurred while requesting OTP.");
    }
  };

  const handleOtpSubmit = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      message.error("Please enter a complete OTP.");
      return;
    }

    setIsLoading(true);

    const signupDetails = JSON.parse(localStorage.getItem("signupDetails"));

    try {
      const response = await fetch("https://api.21stcenturyav.info/signup/user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...signupDetails,
          otp: otpString,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        message.error(`Signup failed: ${data.message}`);
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      localStorage.setItem("accessToken", data.access_token);
      setIsLoading(false);
      message.success("Signup successful.");
      navigate("/HomePage");
    } catch (error) {
      console.error("Signup error:", error);
      setIsLoading(false);
      message.error("An error occurred during signup.");
    }
  };

  const renderOtpInput = () => (
    <Space>
      {otp.map((digit, index) => (
        <Input
          key={index}
          maxLength={1}
          value={digit}
          onChange={(e) => handleOtpChange(e.target.value, index)}
          onKeyDown={(e) => handleOtpKeyDown(e, index)}
          ref={(el) => (otpRefs.current[index] = el)} // Assign ref
          style={{
            width: "40px",
            textAlign: "center",
            fontSize: "18px",
          }}
        />
      ))}
    </Space>
  );

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        backgroundColor: "#f0f2f5",
        padding: "20px",
      }}
    >
      <Card
        style={{
          maxWidth: 600,
          width: "100%",
          borderRadius: "10px",
          boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
        }}
        bodyStyle={{ padding: "40px" }}
      >
        {stage === "verifyOtp" ? (
          <Form layout="vertical" onFinish={handleOtpSubmit}>
            <Title level={3} style={{ textAlign: "center" }}>
              Verify OTP
            </Title>
            <Text>Enter the 6-digit OTP sent to your email</Text>
            <div style={{ marginTop: "16px", marginBottom: "16px" }}>
              {renderOtpInput()}
            </div>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={isLoading} block>
                Verify and Register
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Form layout="vertical" form={form} onFinish={handleRequestOtp}>
            <Title level={3} style={{ textAlign: "center" }}>
              Create Account
            </Title>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Please enter your name" }]}
            >
              <Input placeholder="Enter your name" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter your email" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: "Please enter your password" }]}
            >
              <Input.Password
                placeholder="Enter your password"
                visibilityToggle={true}
              />
            </Form.Item>
            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Please confirm your password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm your password" />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
              >
                Request OTP
              </Button>
            </Form.Item>
            <Text style={{ textAlign: "center", display: "block" }}>
              Already have an account?{" "}
              <a href="#" onClick={() => navigate("/")}>
                Log in
              </a>
            </Text>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default Signup;
